import { Download, Printer } from 'lucide-react';
import { useRef } from 'react';

import { LEGAL_DOCUMENTS } from './documents.config';
import { resolveLocale } from './locale';
import { usePdfAvailability } from './use-pdf-availability';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

/** Static asset path of the eIFU PDF for a locale (see tools/build-legal-docs.mjs). */
export function eifuPdfUrl(locale: string, version: string): string {
    return `/legal/eifu/${locale}/eifu-${version}.pdf`;
}

/**
 * Electronic Instructions for Use page (ESCLOUD-77 / ESCLOUD-181): PDF viewer
 * with download and print actions, and a side panel with the document version,
 * issue date, corresponding software version and the paper-copy notice
 * (Regulation (EU) 2021/2226 Art. 4-5).
 */
export function EifuPage() {
    const config = LEGAL_DOCUMENTS.eifu;
    const locale = resolveLocale(config.locales);
    const pdfUrl = eifuPdfUrl(locale, config.version);
    const { data: pdfAvailable } = usePdfAvailability(pdfUrl);
    const viewerRef = useRef<HTMLIFrameElement>(null);

    const print = () => {
        const viewerWindow = viewerRef.current?.contentWindow;
        if (viewerWindow) {
            // Same-origin PDF: print straight from the embedded viewer.
            viewerWindow.print();
        } else {
            // No embedded viewer to print from — open the PDF in a new tab
            // where the browser's own viewer provides printing.
            window.open(pdfUrl, '_blank', 'noopener');
        }
    };

    return (
        <div className="flex h-full min-h-[70vh] w-full flex-col gap-4 py-6">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-semibold">{config.titles[locale]}</h1>
                <div className="flex gap-2">
                    {pdfAvailable === false ? (
                        // `disabled` has no effect on an asChild anchor — render a
                        // plain disabled button while the PDF is unavailable.
                        <Button variant="outline" size="sm" disabled>
                            <Download /> Download PDF
                        </Button>
                    ) : (
                        <Button variant="outline" size="sm" asChild>
                            <a href={pdfUrl} download={`escloud-eifu-${locale}-${config.version}.pdf`}>
                                <Download /> Download PDF
                            </a>
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={print}
                        disabled={pdfAvailable === false}
                    >
                        <Printer /> Print
                    </Button>
                </div>
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
                {pdfAvailable === false ? (
                    // Probe failed: show the mandated paper-copy alternative rather
                    // than a raw browser error pane inside the iframe.
                    <div className="flex min-h-[60vh] w-full flex-1 items-center justify-center rounded-md border bg-muted/30 p-6">
                        <p className="max-w-md text-center text-sm text-muted-foreground">
                            The electronic Instructions for Use could not be loaded. Please try again
                            later, or request a free paper copy from{' '}
                            <a className="underline" href="mailto:contact@therenva.com">
                                contact@therenva.com
                            </a>{' '}
                            — it will be delivered within 7 calendar days.
                        </p>
                    </div>
                ) : (
                    <iframe
                        ref={viewerRef}
                        src={pdfUrl}
                        title="Instructions for Use"
                        className="min-h-[60vh] w-full flex-1 rounded-md border bg-muted/30"
                    />
                )}

                <aside className="shrink-0 space-y-3 text-sm lg:w-64">
                    <div>
                        <h2 className="font-medium">Document</h2>
                        <dl className="mt-1 space-y-1 text-muted-foreground">
                            <div className="flex justify-between gap-2">
                                <dt>Version</dt>
                                <dd className="text-foreground">{config.version}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                                <dt>Issue date</dt>
                                <dd className="text-foreground">{config.issueDate}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                                <dt>Software version</dt>
                                <dd className="text-foreground">{config.softwareVersion}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                                <dt>Language</dt>
                                <dd className="text-foreground uppercase">{locale}</dd>
                            </div>
                        </dl>
                    </div>
                    <Separator />
                    <p className="text-xs text-muted-foreground">
                        A paper copy of the IFU can be requested from{' '}
                        <a className="underline" href="mailto:contact@therenva.com">
                            contact@therenva.com
                        </a>{' '}
                        and will be delivered free of charge within 7 calendar days.
                    </p>
                </aside>
            </div>
        </div>
    );
}
